<template>
  <o-modal
    ref="modal"
    :title="dialogTitle"
    :options="dialogOptions"
    :loading="loading"
    modal-class="relative m-2 bg-gray-100 dark:bg-gray-950 rounded-lg shadow-lg pointer-events-auto overflow-hidden w-[min(95vw,860px)] h-[min(88vh,720px)]"
    content-class="flex flex-col h-full min-h-0"
  >
    <template #body>
      <div class="grid h-full min-h-0 grid-cols-1 md:grid-cols-[minmax(0,1fr)_17rem]">
        <div class="flex min-h-0 flex-col border-b border-gray-200 dark:border-gray-800 md:border-b-0 md:border-r">
          <div class="border-b border-gray-200 p-3 dark:border-gray-800">
            <segment-field v-model="source" :options="sourceOptions" />
          </div>

          <template v-if="source === 'library'">
            <div class="p-3">
              <text-field
                v-model="search"
                :placeholder="$t('components.elements.email.image.search')"
                leading="search"
                clear
              />
            </div>

            <div class="relative min-h-0 flex-1 overflow-auto px-3 pb-3">
              <div v-if="filteredAssets.length" class="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <button
                  v-for="asset in filteredAssets"
                  :key="asset.url"
                  type="button"
                  class="group overflow-hidden rounded-lg border bg-white text-left transition hover:border-primary-400 hover:shadow-sm dark:bg-gray-900 dark:border-gray-800"
                  :class="selectedUrl === asset.url ? 'ring-2 ring-primary-500 border-primary-500' : ''"
                  @click="selectAsset(asset)"
                >
                  <img
                    :src="asset.thumbnail"
                    :alt="asset.label"
                    class="h-28 w-full bg-gray-100 object-cover dark:bg-gray-800"
                  >
                  <span class="block truncate px-2 py-2 text-xs font-medium text-gray-700 dark:text-gray-200">
                    {{ asset.label }}
                  </span>
                </button>
              </div>

              <div v-else class="flex h-full min-h-40 flex-col items-center justify-center text-center text-gray-500 dark:text-gray-400">
                <UIcon name="i-lucide-images" class="mb-2 size-8" />
                <p class="text-sm font-medium">
                  {{ $t('components.elements.email.image.noMatches') }}
                </p>
              </div>

              <div v-if="assetsLoading" class="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm dark:bg-gray-950/70">
                <o-loader />
              </div>
            </div>

            <o-pagination
              v-if="assetPager?.items.length"
              :key="search"
              :pager="assetPager"
              container-class="border-t flex justify-end items-center shrink-0 px-3 py-2"
            />
          </template>

          <div v-else class="flex min-h-0 flex-1 items-center justify-center overflow-auto p-6">
            <label class="flex w-full max-w-md cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-white p-8 text-center transition hover:border-primary-400 hover:bg-primary-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-primary-950/30">
              <img
                v-if="uploadPreview"
                :src="uploadPreview"
                :alt="alternativeText"
                class="mb-4 max-h-56 max-w-full rounded-lg object-contain"
              >
              <UIcon v-else name="i-lucide-upload-cloud" class="mb-3 size-10 text-gray-400" />
              <span class="font-medium text-gray-800 dark:text-gray-100">
                {{ uploadFile?.name || $t('components.elements.email.image.chooseFile') }}
              </span>
              <span class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {{ $t('components.elements.email.image.chooseFileInfo') }}
              </span>
              <input class="hidden" type="file" accept="image/*" @change="selectUpload">
            </label>
          </div>
        </div>

        <div class="overflow-auto bg-white p-4 dark:bg-gray-900">
          <p class="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
            {{ $t('components.elements.email.image.settings') }}
          </p>

          <div class="space-y-4">
            <div class="flex min-h-32 items-center justify-center overflow-hidden rounded-lg border bg-gray-50 p-2 dark:border-gray-800 dark:bg-gray-950">
              <img
                v-if="imagePreview"
                :src="imagePreview"
                :alt="alternativeText"
                class="max-h-40 max-w-full object-contain"
              >
              <UIcon v-else name="i-lucide-image" class="size-8 text-gray-300 dark:text-gray-700" />
            </div>

            <o-field-group :label="$t('components.elements.email.image.alternativeText')" class="block">
              <text-field v-model="alternativeText" />
            </o-field-group>

            <o-field-group :label="$t('components.elements.email.image.width')" class="block">
              <select-field v-model="width" :options="widthOptions" />
            </o-field-group>

            <o-field-group :label="$t('components.elements.email.image.alignment')" class="block">
              <segment-field v-model="alignment" :options="alignmentOptions" />
            </o-field-group>
          </div>
        </div>
      </div>
    </template>
  </o-modal>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'

import {
  SegmentField,
  SelectField,
  TextField,
} from '@/components/fields'
import OPagination from '@/components/elements/Pagination.vue'
import { TablePager } from '@/_api/lib/pager'
import {
  normaliseEmailEditorAssets,
  type EmailEditorAsset,
} from '@/lib/emailEditor'

interface Props {
  assets?: EmailEditorAsset[]
  upload?: ((upload: any, data: any) => Promise<any>) | null
}

const props = withDefaults(defineProps<Props>(), {
  assets: () => [],
  upload: null,
})

const { $api, $grants, $i18n } = useNuxtApp()
const modal = ref<any>()
const editor = shallowRef<any>(null)
const editing = ref(false)
const source = ref('library')
const search = ref('')
const selectedUrl = ref('')
const uploadFile = ref<File | null>(null)
const uploadPreview = ref('')
const alternativeText = ref('')
const width = ref('100%')
const alignment = ref('center')
const loading = ref(false)
const assetsLoading = ref(false)
const assetPager = shallowRef<TablePager | null>(null)

const sourceOptions = computed(() => [
  { text: $i18n.t('components.elements.email.image.library'), value: 'library' },
  { text: $i18n.t('components.elements.email.image.upload'), value: 'upload' },
])
const widthOptions = computed(() => [
  { text: $i18n.t('components.elements.email.image.widthFull'), value: '100%' },
  { text: '75%', value: '75%' },
  { text: '50%', value: '50%' },
  { text: '25%', value: '25%' },
])
const alignmentOptions = computed(() => [
  { text: $i18n.t('components.elements.email.image.alignLeft'), value: 'left' },
  { text: $i18n.t('components.elements.email.image.alignCentre'), value: 'center' },
  { text: $i18n.t('components.elements.email.image.alignRight'), value: 'right' },
])
const availableAssets = computed(() => assetPager.value
  ? normaliseEmailEditorAssets(assetPager.value.items)
  : props.assets)
const filteredAssets = computed(() => {
  if (assetPager.value) return availableAssets.value

  const query = search.value.trim().toLowerCase()
  if (!query) return availableAssets.value
  return availableAssets.value.filter(asset => asset.label.toLowerCase().includes(query))
})
const imagePreview = computed(() => source.value === 'upload' ? uploadPreview.value : selectedUrl.value)
const valid = computed(() => source.value === 'upload' ? Boolean(uploadFile.value) : Boolean(selectedUrl.value))
const dialogTitle = computed(() => editing.value
  ? $i18n.t('components.elements.email.image.edit')
  : $i18n.t('components.elements.email.image.title'))
const dialogOptions = computed(() => [
  { text: $i18n.t('general.cancel'), type: 'cancel' },
  {
    text: editing.value ? $i18n.t('general.update') : $i18n.t('general.insert'),
    type: 'confirm',
    variant: 'info',
    callback: confirm,
    disabled: !valid.value,
  },
])

watch(source, (value) => {
  if (value === 'library' && selectedUrl.value) return
  if (value === 'upload' && uploadFile.value) return
  alternativeText.value = ''
})

watch(search, debounce(() => {
  if (assetPager.value) void reloadAssets()
}, 300))

function open (editorInstance: any) {
  editor.value = editorInstance
  editing.value = editorInstance.isActive('image')
  search.value = ''
  clearUploadPreview()
  width.value = '100%'
  alignment.value = 'center'
  alternativeText.value = ''
  selectedUrl.value = ''
  source.value = canBrowseAssets() || props.assets.length ? 'library' : 'upload'

  if (canBrowseAssets()) void loadAssets()

  if (editing.value) {
    const attributes = editorInstance.getAttributes('image')
    selectedUrl.value = attributes.src ?? ''
    alternativeText.value = attributes.alt ?? ''
    width.value = attributes.width ?? '100%'
    alignment.value = attributes.alignment ?? 'center'
    source.value = 'library'
  }

  modal.value.open()
}

function canBrowseAssets () {
  return $grants.all({ marketing: ['view'] })
}

async function loadAssets () {
  assetsLoading.value = true
  assetPager.value = new TablePager((page: number) => $api.marketing.uploads({
    page,
    search: search.value || null,
    dataType: 0,
  }), 20)

  try {
    await assetPager.value.load()
  } finally {
    assetsLoading.value = false
  }
}

async function reloadAssets () {
  if (!assetPager.value) return
  assetsLoading.value = true
  try {
    await assetPager.value.reload()
  } finally {
    assetsLoading.value = false
  }
}

function selectAsset (asset: EmailEditorAsset) {
  selectedUrl.value = asset.url
  alternativeText.value = asset.label
}

function selectUpload (event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0] ?? null
  target.value = ''
  clearUploadPreview()
  uploadFile.value = file
  if (!file) return

  uploadPreview.value = URL.createObjectURL(file)
  if (!alternativeText.value) {
    alternativeText.value = file.name.replace(/\.[^.]+$/, '')
  }
}

function clearUploadPreview () {
  if (uploadPreview.value) URL.revokeObjectURL(uploadPreview.value)
  uploadPreview.value = ''
  uploadFile.value = null
}

async function uploadImage (): Promise<string | null> {
  if (!uploadFile.value) return null

  if (typeof props.upload !== 'function') {
    return await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(uploadFile.value as File)
    })
  }

  let uploadedUrl: string | null = null
  const uploadProxy = {
    complete: (image: any) => {
      uploadedUrl = image?.file?.url ?? image?.url ?? null
    },
  }

  await props.upload(uploadProxy, { files: [uploadFile.value], e: null })
  return uploadedUrl
}

async function confirm () {
  if (!editor.value || !valid.value) return false

  loading.value = true
  try {
    const url = source.value === 'upload' ? await uploadImage() : selectedUrl.value
    if (!url) return false

    const attributes = {
      src: url,
      alt: alternativeText.value.trim(),
      width: width.value,
      alignment: alignment.value,
    }

    if (editing.value) {
      editor.value.chain().focus().updateAttributes('image', attributes).run()
    } else {
      editor.value.chain().focus().setImage(attributes).run()
    }

    return true
  } finally {
    loading.value = false
  }
}

onBeforeUnmount(clearUploadPreview)

defineExpose({ open })
</script>
